import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../api/authAPI';
import { getAuthProviderUrl, apiBaseUrl } from '../config/runtime';

/* 카카오/구글 정식 SVG 로고 (LoginPage와 동일) */
const KakaoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#191919">
    <path d="M12 3C6.477 3 2 6.477 2 10.8c0 2.715 1.633 5.1 4.1 6.525L5.1 21l4.55-2.725A11.8 11.8 0 0 0 12 18.6c5.523 0 10-3.477 10-7.8S17.523 3 12 3z"/>
  </svg>
);
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [nicknameChecking, setNicknameChecking] = useState(false);
  const [nicknameAvailable, setNicknameAvailable] = useState<boolean | null>(null);
  
  // SMS 인증 상태
  const [smsCode, setSmsCode] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsTimer, setSmsTimer] = useState(0);
  
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { register } = useAuth();

  // SMS 타이머
  useEffect(() => {
    if (smsTimer > 0) {
      const timer = setTimeout(() => setSmsTimer(smsTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [smsTimer]);

  // 닉네임 중복 확인
  const checkNickname = async (nickname: string) => {
    if (nickname.length < 2) {
      setNicknameAvailable(null);
      return;
    }
    setNicknameChecking(true);
    try {
      const response = await authAPI.checkNickname(nickname);
      setNicknameAvailable(response.available);
    } catch (err) {
      setNicknameAvailable(null);
    } finally {
      setNicknameChecking(false);
    }
  };

  const handleNicknameChange = (value: string) => {
    setNickname(value);
    setNicknameAvailable(null);
    const timeoutId = setTimeout(() => {
      if (value.trim()) checkNickname(value);
    }, 500);
    return () => clearTimeout(timeoutId);
  };

  // 이메일 인증번호 발송 (2026-07-08: SMS → 이메일 인증 전환, 비용 0원)
  const handleSendSms = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('올바른 이메일 주소를 먼저 입력해주세요.');
      return;
    }
    console.log('[Register] 이메일 인증번호 발송 요청:', email);
    setSmsLoading(true);
    setError('');

    try {
      const res = await fetch(apiBaseUrl('/email-verification/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const response = await res.json();
      if (response.success) {
        setSmsSent(true);
        setSmsTimer(180); // 3분 후 재발송 가능
        setSuccess(response.message || '인증번호가 발송되었습니다. 메일함을 확인해주세요.');
        // (2026-08-09) 보안 수정: 백엔드가 더 이상 인증번호를 응답에 담아 보내지 않으므로
        // 자동 입력 로직 제거 — 사용자가 직접 메일함에서 확인해 입력해야 함
      } else {
        setError(response.message || '인증번호 발송에 실패했습니다.');
      }
    } catch (err: any) {
      setError('인증번호 발송에 실패했습니다.');
    } finally {
      setSmsLoading(false);
    }
  };

  // 이메일 인증번호 확인
  const handleVerifySms = async () => {
    if (!smsCode || smsCode.length !== 6) {
      setError('6자리 인증번호를 입력해주세요.');
      return;
    }
    console.log('[Register] 이메일 인증번호 확인');
    setSmsLoading(true);
    setError('');

    try {
      const res = await fetch(apiBaseUrl('/email-verification/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: smsCode }),
      });
      const response = await res.json();
      if (response.success) {
        setSmsVerified(true);
        setSuccess('이메일 인증이 완료되었습니다!');
      } else {
        setError(response.message || '인증번호가 올바르지 않습니다.');
      }
    } catch (err: any) {
      setError('인증 확인에 실패했습니다.');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // 필수 검증
    if (!password || password.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }
    if (!nickname.trim()) {
      setError('닉네임을 입력해주세요.');
      return;
    }
    if (nicknameAvailable === false) {
      setError('사용할 수 없는 닉네임입니다.');
      return;
    }
    if (!smsVerified) {
      setError('이메일 인증을 완료해주세요.');
      return;
    }

    // 생년월일 유효성 검증
    if (birthDate) {
      const date = new Date(birthDate);
      const now = new Date();
      const minDate = new Date('1900-01-01');
      if (date > now || date < minDate) {
        setError('올바른 생년월일을 입력해주세요.');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      await register(email, password, name, nickname, phone, birthDate, gender);
      setSuccess(t('register.success'));
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('register.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider: string) => {
    console.log(`${provider} 소셜 회원가입 시도`);
    // 소셜 로그인 URL로 리다이렉트
    window.location.href = getAuthProviderUrl(provider);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box', marginBottom: '12px',
    background: 'rgba(255,255,255,0.05)', color: '#fff', fontFamily: 'inherit',
    outline: 'none', transition: 'border-color 0.15s',
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#000',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '32px 20px', fontFamily: "'Pretendard', -apple-system, sans-serif",
      position: 'relative', overflow: 'hidden',
    }}>
      {/* 글로우 배경 */}
      <div style={{
        position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)',
        width: 400, height: 250,
        background: 'radial-gradient(ellipse, rgba(124,58,237,0.2) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        maxWidth: 440, width: '100%',
        background: '#0d0d0d',
        border: '1px solid rgba(255,255,255,0.1)',
        padding: '36px 32px', borderRadius: '16px',
        position: 'relative', zIndex: 1, color: '#fff',
      }}>
        <h2 style={{ textAlign: 'center', marginBottom: '6px', color: '#fff', fontSize: '24px', fontWeight: '800', letterSpacing: '-0.05em' }}>
          freematch
        </h2>
        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px', marginBottom: '28px' }}>
          {t('register.subtitle')}
        </p>

        {/* 소셜 로그인 */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '14px', fontSize: '13px', color: 'rgba(255,255,255,0.35)' }}>{t('register.socialTitle')}</div>
          <button onClick={() => handleSocialLogin('kakao')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px', backgroundColor: '#FEE500', color: '#191919', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginBottom: '8px', fontFamily: 'inherit' }}>
            <KakaoIcon />{t('register.kakao')}
          </button>
          <button onClick={() => handleSocialLogin('google')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px', backgroundColor: '#fff', color: '#333', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>
            <GoogleIcon />{t('register.google')}
          </button>
        </div>

        {/* 구분선 */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '22px' }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
          <span style={{ margin: '0 14px', color: 'rgba(255,255,255,0.2)', fontSize: '12px' }}>{t('login.or')}</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
        </div>

        {/* 이메일 폼 */}
        <form onSubmit={handleSubmit}>
          {/* 이메일 + 인증 (필수) */}
          <div style={{ marginBottom: '12px', padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '9px', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '10px', color: 'rgba(255,255,255,0.6)' }}>
              {t('register.emailVerify')}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              <input type="email" placeholder={t('register.email')} value={email} disabled={smsVerified}
                onChange={e => { setEmail(e.target.value); if (smsSent || smsVerified) { setSmsSent(false); setSmsVerified(false); setSmsCode(''); setSmsTimer(0); } }}
                required style={{ ...inputStyle, marginBottom: 0, flex: 1, opacity: smsVerified ? 0.5 : 1 }} />
              <button type="button" onClick={handleSendSms} disabled={smsLoading || smsVerified || smsTimer > 0} style={{ padding: '11px 14px', background: smsVerified ? 'rgba(52,211,153,0.2)' : smsTimer > 0 ? 'rgba(255,255,255,0.06)' : 'rgba(96,165,250,0.2)', color: smsVerified ? '#34d399' : smsTimer > 0 ? 'rgba(255,255,255,0.35)' : '#60a5fa', border: '1px solid', borderColor: smsVerified ? 'rgba(52,211,153,0.3)' : smsTimer > 0 ? 'rgba(255,255,255,0.1)' : 'rgba(59,130,246,0.3)', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: smsVerified || smsTimer > 0 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                {smsVerified ? t('register.done') : smsTimer > 0 ? `${Math.floor(smsTimer/60)}:${String(smsTimer%60).padStart(2,'0')}` : smsSent ? t('register.resend') : t('register.sendCode')}
              </button>
            </div>
            {smsSent && !smsVerified && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <input type="text" placeholder={t('register.codePlaceholder')} value={smsCode} onChange={e => setSmsCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} maxLength={6} style={{ ...inputStyle, marginBottom: 0, flex: 1, textAlign: 'center', letterSpacing: '6px' }} />
                <button type="button" onClick={handleVerifySms} disabled={smsLoading || smsCode.length !== 6} style={{ padding: '11px 16px', background: smsCode.length === 6 ? 'rgba(52,211,153,0.2)' : 'rgba(255,255,255,0.05)', color: smsCode.length === 6 ? '#34d399' : 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: smsCode.length === 6 ? 'rgba(52,211,153,0.3)' : 'rgba(255,255,255,0.08)', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: smsCode.length === 6 ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
                  {t('register.verify')}
                </button>
              </div>
            )}
            {smsVerified && <div style={{ color: '#34d399', fontSize: '13px', marginTop: '6px' }}>{t('register.verified')}</div>}
          </div>

          <input type="password" placeholder={t('register.password')} value={password} onChange={e => setPassword(e.target.value)} required minLength={6} style={inputStyle} onFocus={e => e.target.style.borderColor = 'rgba(167,139,250,0.5)'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'} />

          <input type="text" placeholder={t('register.name')} value={name} onChange={e => setName(e.target.value)} required style={inputStyle} onFocus={e => e.target.style.borderColor = 'rgba(167,139,250,0.5)'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'} />

          <div style={{ marginBottom: '12px', position: 'relative' }}>
            <input type="text" placeholder={t('register.nickname')} value={nickname} onChange={e => handleNicknameChange(e.target.value)} required style={{ ...inputStyle, marginBottom: 0, borderColor: nicknameAvailable === true ? 'rgba(52,211,153,0.5)' : nicknameAvailable === false ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)' }} />
            {nicknameChecking && <span style={{ position: 'absolute', right: '13px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>확인중...</span>}
            {nicknameAvailable !== null && !nicknameChecking && <span style={{ position: 'absolute', right: '13px', top: '50%', transform: 'translateY(-50%)' }}>{nicknameAvailable ? '✅' : '❌'}</span>}
          </div>

          {/* 휴대폰 번호 (선택 — 인증 없음, SMS 인증은 이메일 인증으로 대체됨) */}
          <input type="tel" placeholder={t('register.phone')} value={phone} onChange={e => setPhone(e.target.value.replace(/[^0-9]/g, ''))} style={inputStyle} onFocus={e => e.target.style.borderColor = 'rgba(167,139,250,0.5)'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'} />

          <input type="date" value={birthDate} onChange={e => { const val = e.target.value; setBirthDate(val); if (val) { const date = new Date(val); const now = new Date(); if (date > now) { setError('생년월일은 오늘 이후 날짜를 입력할 수 없습니다.'); } else { setError(''); } } }} min="1900-01-01" max={new Date().toISOString().split('T')[0]} style={{ ...inputStyle, colorScheme: 'dark' }} />

          <select value={gender} onChange={e => setGender(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">{t('register.genderSelect')}</option>
            <option value="male">{t('register.male')}</option>
            <option value="female">{t('register.female')}</option>
            <option value="other">{t('register.other')}</option>
          </select>

          <button type="submit" disabled={loading || nicknameAvailable === false || !smsVerified} style={{ width: '100%', padding: '13px', background: loading || nicknameAvailable === false || !smsVerified ? 'rgba(255,255,255,0.3)' : '#fff', color: '#000', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: loading || !smsVerified ? 'not-allowed' : 'pointer', marginBottom: '18px', fontFamily: 'inherit', transition: 'opacity 0.15s' }}>
            {loading ? '가입 중...' : t('register.submit')}
          </button>

          <div style={{ textAlign: 'center' }}>
            <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '13px' }}>{t('register.haveAccount')} </span>
            <button type="button" onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: '#a78bfa', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' }}>
              {t('register.login')}
            </button>
          </div>

          {error && <div style={{ color: '#fca5a5', marginTop: '16px', padding: '11px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', textAlign: 'center', fontSize: '13px' }}>{error}</div>}
          {success && <div style={{ color: '#6ee7b7', marginTop: '16px', padding: '11px 14px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '8px', textAlign: 'center', fontSize: '13px' }}>{success}</div>}
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
